<template>
  <div class="recordings-detail-container" :class="{ 'is-expanded': expanded }">

    <!-- BODY: tester label + recording title + direct message + buttons -->
    <div class="recordings-detail-body">
      <div class="recordings-detail-header-row">
        <div class="tester-inline-row" v-if="this.testerList.length != 0">
          <span class="playtester-label">Playtester:</span>
          <span class="playtester-name">{{ this.currTester }}</span>
          <button class="survey-btn message-inline-button" @click="handleMessageTester">Message</button>
        </div>
        <div v-else class="tester-inline-row muted-text">
          No testers assigned to play session.
        </div>

        <div class="recordings-detail-buttons">
          <b-button
            class="recordings-detail-button"
            v-if="individual == true"
            v-on:click="downloadVideo"
          >
            Download
          </b-button>
          <b-button
            class="recordings-detail-button expand-toggle-button"
            v-on:click="$emit('toggle-expand')"
          >
            {{ expanded ? 'Minimize' : 'Expand' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- VIDEO + TIMESTAMP SIDEBAR -->
    <div class="video-and-timestamps">

      <!-- Video player -->
      <div class="video-zone">
        <div class="video-action-bar">
          <div class="recording-inline-row">
            <div class="recordings-detail-title">
              <div class="recordings-detail-title-text">Results</div>
            </div>
            <div class="recordings-detail-name">
              <div class="recordings-detail-name-text" v-if="recording.name">
                {{ recording.name }}
              </div>
              <div class="recordings-detail-name-text" v-else>No Name</div>
            </div>
          </div>

          <div class="video-nav-buttons" v-if="this.individual == true && this.testerList.length != 0">
            <button
              class="survey-btn mini-nav-button"
              :disabled="currTesterID - 1 < 0"
              v-on:click="getCurrTester(currTesterID - 1)"
            >
              Prev
            </button>
            <button
              class="survey-btn mini-nav-button"
              :disabled="currTesterID + 1 >= testerList.length"
              v-on:click="getCurrTester(currTesterID + 1)"
            >
              Next
            </button>
          </div>
        </div>

        <div class="video-container">
          <video
            ref="recordedVideo"
            class="video-frame"
            v-if="videoUrl"
            controls
            :key="videoUrl"
            :src="videoUrl"
            width="100%"
            height="100%"
            @loadedmetadata="onLoadedMetadata"
            @timeupdate="onTimeUpdate"
          ></video>
          <div class="placeholder-overlay" v-else>
            <span>No recording available</span>
          </div>
        </div>
      </div>

      <!-- Timestamp sidebar -->
      <div
        class="timestamp-sidebar"
        v-if="videoUrl && getInstructions(3).length > 0"
      >
        <div class="timestamp-sidebar-title">Timestamps</div>
        <div
          v-for="item in getInstructions(3)"
          :key="item.id"
          class="timestamp-entry"
          :class="{ 
            'timestamp-bug': item.OGList === 2, 
            'timestamp-entry-expanded': isNoteExpanded(item.id),
            'timestamp-entry-maximized': maximizedNoteId === item.id
          }"
          @click="toggleNoteExpand(item.id)"
        >
          <div class="timestamp-entry-top">
            <span class="timestamp-time" @click.stop="currentTime = item.timeStamp; seekVideo()">
              {{ formatTime(item.timeStamp) }}
              <span v-if="item.OGList === 2" class="timestamp-title">{{ item.title || 'Untitled' }}</span>
            </span>
            <div class="timestamp-controls">
              <span class="timestamp-expand-toggle" @click.stop="toggleNoteExpand(item.id)">
                {{ isNoteExpanded(item.id) ? '▲' : '▼' }}
              </span>
              <span class="timestamp-maximize-toggle" @click.stop="toggleMaximize(item.id)">
                {{ maximizedNoteId === item.id ? '✕' : '⛶' }}
              </span>
            </div>
          </div>
          <span
            class="timestamp-label"
            :class="{ 'timestamp-label-expanded': isNoteExpanded(item.id) || maximizedNoteId === item.id }"
            @click.stop="toggleNoteExpand(item.id)"
          >{{ item.instruction || 'Untitled' }}</span>
        </div>
      </div>

    </div>

    <!-- SLIM TIMELINE -->
    <div v-if="videoUrl" class="timeline-container">
      <div class="time-labels">
        <span>{{ formatTime(currentTime) }}</span>
        <span>{{ formatTime(videoDuration) }}</span>
      </div>

      <div class="timeline-track-wrapper">
        <input
          type="range"
          min="0"
          :max="videoDuration"
          step="0.01"
          v-model="currentTime"
          @input="seekVideo"
          class="timeline-slider"
        />

        <div
          class="drop-container visual-timeline"
          @dragenter.prevent
          @dragover.prevent
        >
          <p v-if="getInstructions(3).length === 0" class="drop-hint">
            Drag instructions or bugs here to pin them to the timeline
          </p>
          <div
            v-for="item in getInstructions(3)"
            :key="item.id"
            class="timeline-marker"
            :style="{ left: (item.timeStamp / videoDuration * 100) + '%' }"
            @click="currentTime = item.timeStamp; seekVideo()"
          >
            <div class="marker-pin" :class="{ 'pin-bug': item.OGList === 2 }"></div>
            <div v-if="item.OGList !== 2" class="marker-tooltip">
              <strong>{{ formatTime(item.timeStamp) }}</strong>: {{ item.instruction || 'Untitled' }}
            </div>
            <div v-else class="marker-tooltip">
              <strong>{{ formatTime(item.timeStamp) }}</strong>: {{ item.title || 'Untitled' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <MessagePanel ref="messagePanel" :contacts="messageContacts" :userType="userType" />

  </div>
</template>

<script>

import firebase from "firebase";
import GameTesting from "../../../Firestore/GameTesting";
import UserObject from "../../../Firestore/UserObject";
import { ref } from 'vue';
import { db } from '../../../db.js';
import Chat from '../../../Firestore/Chat.js';
import MessagePanel from '../../messagebox/developer-messagepanel.vue';

export default {
  name: "results",
  components: { MessagePanel },
  props: {
    detail: {
      type: Object
    },
    recording: {
      type: Object
    },
    expanded: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      testerList: [],
      currTester: null,
      currTesterID: 0,
      individual: true,
      currentTime: 0,
      duration: 0,
      videoDuration: 0,
      videoUrl: null,
      items: [],
      instrItems: [],
      bugItems: [],
      fetchedInstruction: null,
      expandedNoteIds: [], // ids of timestamp entries the user has expanded to show full text
      maximizedNoteId: null, // id of note that is maximized/enlarged
      messageContacts: [],
      isLoadingMessage: false,
      userType: window.localStorage.getItem("userType") ? parseInt(window.localStorage.getItem("userType")) : -1,
    };
  },
  watch: {
    recording(newRecording, oldRecording) {
      this.testerList = this.recording['testerIDs'];
      this.getCurrTester(this.currTesterID);
    }
  },
  mounted() {
    this.testerList = this.recording['testerIDs'];
    this.getCurrTester(this.currTesterID);
  },
  methods: {
    seekVideo() {
      const vid = this.$refs.recordedVideo;
      if (vid) vid.currentTime = this.currentTime;
    },
    formatTime(seconds) {
      if (!seconds) return "00:00";
      const m = Math.floor(seconds / 60).toString().padStart(2, "0");
      const s = Math.floor(seconds % 60).toString().padStart(2, "0");
      return `${m}:${s}`;
    },
    getInstructions(list) {
      let filtered = this.items.filter(function(item) { return item.list === list; });
      if (list === 3) {
        return filtered.sort(function(a, b) { return a.timeStamp - b.timeStamp; });
      }
      return filtered;
    },
    isNoteExpanded(id) {
      return this.expandedNoteIds.includes(id);
    },
    toggleNoteExpand(id) {
      const idx = this.expandedNoteIds.indexOf(id);
      if (idx >= 0) {
        this.expandedNoteIds.splice(idx, 1);
      } else {
        this.expandedNoteIds.push(id);
      }
    },
    toggleMaximize(id) {
      if (this.maximizedNoteId === id) {
        this.maximizedNoteId = null;
      } else {
        this.maximizedNoteId = id;
      }
    },
    async loadInstructions() {
      try {
        await GameTesting.loadCompletedTestSession(
          this.recording['gameID'],
          this.testerList[this.currTesterID],
          this.recording['playPeriodID']
        ).then(res => {
          //this does not handle an error for when instructions are empty, todo:
            console.log(res.data[1]? true : false);
            if (res.data[1]){
              this.instrItems = res.data[1].instructions;
              this.bugItems = res.data[1].bugs;
            }else{
              this.instrItems = res.data[0].instructions;
              this.bugItems = res.data[0].bugs;
            }
        });
        this.items = [...this.instrItems, ...this.bugItems];
        //console.log("instr items: ", this.instrItems);
      } catch (error) {
        console.error("Error loading instructions:", error);
      }
    },
    toMainMenu() {
      this.$emit("toMainMenuChild");
    },
    async getCurrTester(testerIndex) {
      //console.log(this.testerList);
      if (this.testerList.length > 0) {
        if (testerIndex >= this.testerList.length) {
          testerIndex = 0;
        } else if (testerIndex < 0) {
          testerIndex = this.testerList.length - 1;
        }
        this.currTesterID = testerIndex;
        this.expandedNoteIds = []; // reset expanded notes when switching testers
        this.maximizedNoteId = null; // reset maximized note when switching testers
        //console.log(this.currTesterID);
        UserObject.getProfile(this.testerList[this.currTesterID]).then(async res => {
          this.currTester = res.data['userName'];
          await this.loadVideoData();
          await this.loadInstructions();
        });
      }
    },
    switchViews() {
      this.individual = !this.individual;
    },
    async loadVideoData() {
      try {
        //console.log(this.testerList[this.currTesterID]);
        await GameTesting.loadCompletedTestSession(
          this.recording['gameID'],
          this.testerList[this.currTesterID],
          this.recording['playPeriodID']
        )
          .then(res => {
            const recording = res.data[0].videoRecording;
            this.videoUrl = recording.url || null;
            this.videoDuration = recording.duration || 0;
            /* 
              console.log(res.data[0]);
              console.log(recording.url);
              console.log(recording.duration);
            */
          })
          .catch((error) => {
            const videoEl = this.$refs.recordedVideo;
            if (videoEl) {
              videoEl.pause();
              videoEl.removeAttribute('src');
              videoEl.load();
            }
            this.videoUrl = null;
            this.duration = 0;
            this.videoDuration = 0;
            this.currentTime = 0;
            console.log("Error has occured: " + error);
          });
      } catch (error) {
        console.error("Error loading video data:", error);
        this.videoUrl = null;
      }
    },
    onLoadedMetadata(event) {
      this.duration = event.target.duration;
      //this.videoDuration = event.target.duration;
    },
    onTimeUpdate(event) {
      this.currentTime = event.target.currentTime;
    },
    async downloadVideo() {
      if (!this.videoUrl) {
        alert('No video available to download.');
        return;
      }
      try {
        const a = document.createElement('a');
        a.href = this.videoUrl;
        a.target = '_blank'; // opens in new tab if browser blocks download
        const filename = `${this.recording.name || 'recording'}_${this.currTester || 'tester'}.mp4`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        console.error('Error downloading video:', error);
        alert('Failed to download video. Please try again.');
      }
    },
    async handleMessageTester() {
      const testerId = this.testerList[this.currTesterID];
      if (!testerId) return;
      try {
        this.isLoadingMessage = true;
        const developerId = window.localStorage.getItem('userId');
        if (!developerId) { alert('User not logged in.'); this.isLoadingMessage = false; return; }

        const convResult = await Chat.getOrCreateConversation(testerId, developerId);
        if (!convResult.success) { alert('Failed to open chat.'); this.isLoadingMessage = false; return; }

        const userResult = await Chat.getUserInfo(testerId);
        if (!userResult.success) { alert('Failed to load tester info.'); this.isLoadingMessage = false; return; }

        const contact = {
          id: testerId,
          conversationId: convResult.data.conversationId,
          name: userResult.data.nickName || userResult.data.userName || 'Tester',
          avatar: userResult.data.img || require('../../../assets/avator_example.jpg'),
          lastMessage: convResult.data.lastMessage || 'Start a conversation',
          lastMessageAt: convResult.data.lastMessageAt || new Date(),
          unreadCount: convResult.data.unreadCount || {},
          messages: []
        };

        this.messageContacts = [contact];
        this.isLoadingMessage = false;
        this.$nextTick(() => {
          if (this.$refs.messagePanel) this.$refs.messagePanel.openChatWithContact(contact);
        });
      } catch (error) {
        console.error(error);
        this.isLoadingMessage = false;
      }
    }
  }
};
</script>

<style scoped>

/* ── BUTTONS ── */
.survey-btn,
.survey-btn-add {
  background-color: #fc7727 !important;
  color: #fff;
  border: none;
  border-radius: 10px !important;
}

.survey-btn-add {
  width: 75%;
  margin-bottom: 3%;
  padding: 1%;
}

.survey-btn {
  height: fit-content;
}

.survey-btn:hover:not(:disabled),
.survey-btn-add:hover {
  background-color: #ff8e4d !important;
}

/* ── MISC TEXT ── */
.survey-title {
  color: #000 !important;
  font-size: large;
}

.new-text {
  font-style: italic;
  color: #bebebe;
}

.viewresults-back {
  height: 21px;
  font-size: 16px;
  line-height: 21px;
  text-decoration-line: underline;
  color: #000000;
  margin-bottom: 34px;
  cursor: pointer;
}

.white-text {
  color: #000000;
}

/* ── CONTAINER ── */
.recordings-detail-container {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  width: 100%;
  min-height: 100%;
  padding-top: 2px;
  padding-bottom: 14px;
  overflow-x: hidden;
  box-sizing: border-box;
  position: relative;
}

.recordings-detail-container.is-expanded {
  height: 100%;
}

.recordings-detail-title {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
}

.recordings-detail-title-text {
  font-family: Lato;
  font-style: normal;
  font-weight: bold;
  font-size: 20px;
  line-height: 1.2;
  color: #fc7727;
  vertical-align: bottom;
}

.recordings-detail-buttons {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  z-index: 2;
}

.recordings-detail-button {
  background-color: #fc7727 !important;
  color: #fff;
  border: none;
  border-radius: 8px !important;
  padding: 0.30rem 0.65rem;
  font-size: 0.90rem;
  box-shadow: 3px 3px 5px rgba(0, 0, 0, 0.2);
}

.recordings-detail-button:hover {
  background-color: #ff8e4d !important;
}

.expand-toggle-button {
  margin-left: 0;
}

/* ── SUBTITLE ── */
.recordings-detail-subtitle {
  font-family: Lato;
  font-style: normal;
  font-size: 20px;
  color: #fc7727;
  line-height: 46px;
  margin-top: 5px;
}

.category {
  font-weight: bold;
}

/* ── BODY ── */
.recordings-detail-body {
  margin-left: 5%;
  margin-right: 5%;
  overflow-y: visible;
  margin-bottom: 2px;
}

.recordings-detail-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.tester-inline-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  position: relative;
  top: 15px;
}

.playtester-label {
  color: #fc7727;
  font-size: 15px;
  font-weight: 700;
  font-family: Lato;
}

.playtester-name {
  color: #000;
  font-size: 15px;
  font-weight: 700;
  font-family: Lato;
  font-style: italic;
}

.muted-text {
  color: #8d8d8d;
  font-size: 15px;
}

.recording-inline-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.recordings-detail-name {
  font-family: Lato;
  font-style: italic;
  font-weight: bold;
  margin-left: 10px;
  color: #909090;
}

/* Scrollbar styles inherited from global App.vue */

.recordings-detail-description-text {
  font-family: Lato;
  color: #bebebe;
}

.message-inline-button {
  padding: 0.25rem 0.7rem;
  font-size: 0.75rem;
  line-height: 1.2;
  border-radius: 8px !important;
  min-width: 0;
  height: auto;
  margin-left: 4px;
}

/* ── VIDEO + SIDEBAR WRAPPER ── */
.video-and-timestamps {
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin: 26px 0 8px 5%;
  align-items: stretch;
}

.is-expanded .video-and-timestamps {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.video-zone {
  position: relative;
  flex: 1;
  min-width: 0;
}

.is-expanded .video-zone {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.video-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 0 0 4px;
  flex-shrink: 0;
}

.video-nav-buttons {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.mini-nav-button {
  padding: 0.28rem 0.7rem;
  font-size: 0.8rem;
  line-height: 1.25;
  border-radius: 8px !important;
  min-width: 0;
  height: auto;
  box-shadow: 1px 2px 4px rgba(0, 0, 0, 0.18);
}

/* ── VIDEO CONTAINER ── */
.video-container {
  background: #1a1a1a;
  border-radius: 10px;
  overflow: hidden;
  flex: 1;
  aspect-ratio: 16 / 9;
  position: relative;
  min-width: 0;
}

.is-expanded .video-container {
  aspect-ratio: unset;
  min-height: 0;
}

.video-frame {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.placeholder-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #888;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.05);
}

/* ── TIMESTAMP SIDEBAR ── */
.timestamp-sidebar {
  width: 180px;
  flex-shrink: 0;
  height: 100%;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

.timestamp-sidebar-title {
  font-weight: bold;
  font-size: 13px;
  color: #fc7727;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 4px;
}

.timestamp-entry {
  display: flex;
  flex-direction: column;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 4px;
  transition: background 0.15s;
  user-select: none;
  width: 100%;
  box-sizing: border-box;
}

.timestamp-entry:hover {
  background: #fff4ee;
}

.timestamp-entry-expanded {
  background: #fff4ee;
}

.timestamp-entry-maximized {
  background: #fff4ee;
  padding: 6px 12px 12px 12px;
  margin-bottom: 8px;
  min-height: 120px;
  border: 2px solid #ff7222;
}

.timestamp-entry-maximized .timestamp-label {
  font-size: 14px !important;
  line-height: 1.5 !important;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  color: #333;
  font-weight: 500;
}

.timestamp-entry-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-shrink: 0;
  height: 20px;
  margin-bottom: 4px;
}

.timestamp-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  flex-basis: auto;
}

.timestamp-time {
  font-size: 11px;
  font-weight: bold;
  color: #fc7727;
  flex-shrink: 0;
  flex-grow: 0;
  flex-basis: auto;
  min-width: 35px;
}

.timestamp-expand-toggle {
  font-size: 9px;
  color: #aaa;
  padding: 2px 4px;
  cursor: pointer;
  transition: color 0.2s ease;
  flex-shrink: 0;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 16px;
}

.timestamp-expand-toggle:hover {
  color: #fc7727;
}

.timestamp-entry-expanded .timestamp-expand-toggle {
  color: #db3360;
}

.timestamp-entry-expanded .timestamp-expand-toggle:hover {
  color: #ff1493;
}

.timestamp-maximize-toggle {
  font-size: 10px;
  color: #aaa;
  padding: 2px 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 16px;
}

.timestamp-maximize-toggle:hover {
  color: #ff7222;
}

.timestamp-entry-maximized .timestamp-maximize-toggle {
  color: #ff7222;
  background-color: rgba(255, 114, 34, 0.2);
}

.timestamp-title{
  font-size: 11px;
  color: #7f1c36;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-left: 0.5rem;
}

.timestamp-label {
  font-size: 11px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.timestamp-label-expanded {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}

.timestamp-bug .timestamp-time {
  color: #db3360;
}

/* ── SLIM TIMELINE ── */
.timeline-container {
  margin: 0 5% 10px 5%;
  background: #fdfdfd;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 8px 12px;
  box-sizing: border-box;
}

.time-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 6px;
}

.timeline-track-wrapper {
  position: relative;
  padding-top: 4px;
}

.timeline-slider {
  width: 100%;
  accent-color: #ff7222;
  cursor: pointer;
  position: relative;
  z-index: 5;
  margin: 0;
}

.visual-timeline {
  position: relative;
  height: 28px;
  border: 1px dashed #e0e0e0;
  border-radius: 6px;
  margin-top: 8px;
  background: #fafafa;
  transition: border-color 0.2s;
}

.visual-timeline:hover {
  border-color: #ff7222;
}

.drop-hint {
  width: 100%;
  text-align: center;
  color: #aaa;
  font-size: 0.7rem;
  line-height: 28px;
  margin: 0;
}

/* ── TIMELINE MARKERS ── */
.timeline-marker {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(255, 114, 34, 0.2);
  cursor: pointer;
}

.timeline-marker:hover {
  background: #ff7222;
  z-index: 10;
}

.marker-pin {
  width: 8px;
  height: 8px;
  background: #ff7222;
  border: 2px solid white;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.pin-bug {
  background: #db3360;
}

.marker-tooltip {
  position: absolute;
  bottom: 36px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  font-size: 10px;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
  z-index: 20;
}

.timeline-marker:hover .marker-tooltip {
  opacity: 1;
}

</style>


// WEBPACK FOOTER //
// src/components/game-detail/Recordings/RecordingResults.vue